import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Link2, Loader2, Info } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLogout } from "@/hooks/useLogout";
import { useUserAvatar } from "@/hooks/useUserAvatar";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';
import { NotificationBell } from "./NotificationBell";
import { UserAvatarMenu } from "./UserAvatarMenu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ReactNode, useState } from "react";
import { useToast } from "@/hooks/useToast";

interface HeaderProps {
  pageTitle?: ReactNode;
  pageDescription?: string;
  showBackButton?: boolean;
  showCtaButton?: boolean;
  headerAction?: ReactNode;
  /**
   * Versione ridotta della CTA per gli schermi stretti: un pulsante rotondo con la sola
   * icona. Sotto `md` la barra non ha spazio per un pulsante con testo, ma l'azione
   * principale di una pagina deve restare raggiungibile.
   */
  headerActionCompact?: ReactNode;
}

const Header = ({
  pageTitle = "Dashboard",
  pageDescription = "Welcome to your dashboard",
  showBackButton = true,
  showCtaButton = false,
  headerAction,
  headerActionCompact,
}: HeaderProps) => {
  const { user, userRole } = useAuth();
  const { logout, isLoggingOut } = useLogout();
  const { displayName, avatarUrl } = useUserAvatar();
  const navigate = useNavigate();
  const { toggleMobileSidebar, isSidebarCollapsed } = useSidebar();
  const { t } = useI18n();
  const { toast } = useToast();
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  const handleLogout = () => {
    logout("/login");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCopyTripRequestLink = async () => {
    if (!user) return;
    setIsCopyingLink(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    const path =
      userRole === "travel_designer" || userRole === "travel_advisor_member" || userRole === "agency"
        ? `/#/request-trip?td=${user.id}`
        : "/#/request-trip";
    const url = `${window.location.origin}${path}`;
    await navigator.clipboard.writeText(url);
    setIsCopyingLink(false);
    toast({ variant: 'info', title: t("common.linkCopied") });
  };

  return (
    <header
      className={cn(
        "fixed top-0 z-50 bg-white border-b border-gray-100 transition-all duration-300 ease-in-out",
        // Mobile: full width, Desktop: positioned relative to sidebar
        "left-0 right-0 md:left-auto md:right-0",
        // Desktop positioning based on sidebar state
        isSidebarCollapsed ? "md:left-[70px]" : "md:left-[222px]",
      )}
    >
      <div className="mx-auto px-4">
        <div className="flex items-center justify-between h-20 md:h-[80px]">
          {/* Mobile Title: il sottotitolo ruberebbe due righe su uno schermo stretto,
              quindi si sposta dentro la "i" accanto al titolo. È un Popover e non un
              Tooltip perché su touch non c'è hover: al tap il tooltip non si aprirebbe. */}
          <div className="flex min-w-0 items-center gap-1.5 md:hidden">
            {/* Apertura del menu in testa alla barra: è la prima cosa che si incontra
                leggendo da sinistra, e libera il pollice dal bordo opposto. */}
            {user && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleMobileSidebar}
                className="mr-2 h-10 w-10 flex-shrink-0 rounded-full p-0"
                aria-label={t("navigation.menu")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <h4 className="truncate text-gray-900">{pageTitle}</h4>
            {pageDescription && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 flex-shrink-0 rounded-full p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label={pageDescription}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-3 text-sm text-gray-600">
                  {pageDescription}
                </PopoverContent>
              </Popover>
            )}

            {/* L'azione della pagina sta accanto al suo titolo, non in fondo alla barra
                insieme a notifiche e profilo, che invece valgono ovunque. */}
            {user && (headerActionCompact || (showCtaButton && (
              <Button
                variant="default"
                size="sm"
                className="h-8 flex-shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs"
                disabled={isCopyingLink}
                onClick={handleCopyTripRequestLink}
              >
                {isCopyingLink
                  ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <Link2 className="mr-1 h-3.5 w-3.5" />}
                {t("navigation.shareTripRequestLink")}
              </Button>
            )))}
          </div>

          {/* Desktop Page Title and Description */}
          <div className="hidden md:block">
            <h3 className="text-gray-900">
              {/* {showBackButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-8 w-8 p-0 hover:bg-gray-100 mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )} */}
              {pageTitle}
            </h3>
            <p className="text-sm text-gray-600">{pageDescription}</p>
          </div>

          {/* User Menu Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {user && (
              <>
                {/* Custom Header Action or Default New Trip Button - Only on Desktop */}
                <div className="relative hidden md:block">
                  {headerAction || (showCtaButton && (
                    <Button
                      variant="default"
                      size="lg"
                      className="h-8 px-3"
                      disabled={isCopyingLink}
                      onClick={handleCopyTripRequestLink}
                    >
                      {isCopyingLink
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Link2 className="h-4 w-4 mr-1" />}
                      {t("navigation.shareTripRequestLink")}
                    </Button>
                  ))}
                </div>

                <NotificationBell />

                {/* User Avatar & Dropdown */}
                <UserAvatarMenu
                  displayName={displayName}
                  email={user.email ?? ''}
                  avatarUrl={avatarUrl}
                  onLogout={handleLogout}
                  isLoggingOut={isLoggingOut}
                />

              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
